/**
 * Password policy and strong-password generation for user creation and reset.
 * Best practice: minimum length, mixed case, digit, and special character.
 */

export const PASSWORD_MIN_LENGTH = 8;
export const PASSWORD_SPECIAL_CHARS = '@$!%*#?&';
/** Human-readable policy description for helper text. */
export const PASSWORD_POLICY_DESCRIPTION =
    `At least ${PASSWORD_MIN_LENGTH} characters, with uppercase, lowercase, a digit, and a special character (${PASSWORD_SPECIAL_CHARS}).`;

/** Regex: at least one lowercase, one uppercase, one digit, one special from our set. */
const HAS_LOWER = /[a-z]/;
const HAS_UPPER = /[A-Z]/;
const HAS_DIGIT = /\d/;
const HAS_SPECIAL = new RegExp(`[${PASSWORD_SPECIAL_CHARS.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}]`);

export interface PasswordValidationResult {
    valid: boolean;
    message?: string;
}

/**
 * Validates that a password meets the policy (length + complexity).
 */
export function validatePasswordPolicy(password: string): PasswordValidationResult {
    if (!password || password.length < PASSWORD_MIN_LENGTH) {
        return { valid: false, message: `Use at least ${PASSWORD_MIN_LENGTH} characters.` };
    }
    if (!HAS_LOWER.test(password)) {
        return { valid: false, message: 'Include at least one lowercase letter.' };
    }
    if (!HAS_UPPER.test(password)) {
        return { valid: false, message: 'Include at least one uppercase letter.' };
    }
    if (!HAS_DIGIT.test(password)) {
        return { valid: false, message: 'Include at least one digit.' };
    }
    if (!HAS_SPECIAL.test(password)) {
        return { valid: false, message: `Include at least one special character (${PASSWORD_SPECIAL_CHARS}).` };
    }
    return { valid: true };
}

const CHARS_LOWER = 'abcdefghijklmnopqrstuvwxyz';
const CHARS_UPPER = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
const CHARS_DIGIT = '0123456789';
const CHARS_SPECIAL = PASSWORD_SPECIAL_CHARS;
const CHARS_ALL = CHARS_LOWER + CHARS_UPPER + CHARS_DIGIT + CHARS_SPECIAL;

/**
 * Generates a strong password that satisfies the policy (14 chars by default,
 * at least one of each required type). Uses crypto.getRandomValues when available.
 */
export function generateStrongPassword(length = 14): string {
    const pick = (source: string): string =>
        source[getRandomInt(0, source.length - 1)];
    const arr: string[] = [];
    arr.push(pick(CHARS_LOWER));
    arr.push(pick(CHARS_UPPER));
    arr.push(pick(CHARS_DIGIT));
    arr.push(pick(CHARS_SPECIAL));
    const remaining = length - 4;
    for (let i = 0; i < remaining; i++) {
        arr.push(pick(CHARS_ALL));
    }
    return shuffleArray(arr).join('');
}

function getRandomInt(min: number, max: number): number {
    const range = max - min + 1;
    if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
        const buf = new Uint32Array(1);
        crypto.getRandomValues(buf);
        return min + (buf[0] % range);
    }
    return Math.floor(Math.random() * range) + min;
}

function shuffleArray<T>(arr: T[]): T[] {
    const out = [...arr];
    for (let i = out.length - 1; i > 0; i--) {
        const j = getRandomInt(0, i);
        [out[i], out[j]] = [out[j], out[i]];
    }
    return out;
}
